/**
 * A switch you have to mean: press and hold to flip it.
 *
 * For switches whose flip reaches other people - deploying an alert, say -
 * where a stray click should cost nothing. Holding slides the knob across the
 * track at the speed of the hold, the colour running with it; letting go
 * early springs it back and nothing happens. When the fill completes the switch fires `hold-commit` with the
 * state it should now be in, and shows itself busy until `checked` changes
 * (or a few seconds pass), because the owner decides whether the flip stuck.
 *
 * A plain click does not flip it; it says "Hold to …" just below the pointer.
 * Space and Enter work the same way as a pointer: hold the key.
 *
 * Colour: `--hs-on` (defaults to the accent), so a feature can wear its own.
 */

import { html, type PropertyValues, type TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { LightElement } from '../lit-base.ts';

@customElement('hold-switch')
export class HoldSwitch extends LightElement {
  @property({ type: Boolean }) checked = false;
  @property({ type: Boolean }) disabled = false;
  /** How long a hold has to last, in milliseconds. */
  @property({ type: Number, attribute: 'hold-ms' }) holdMs = 900;
  /** What the switch controls, for screen readers: "Deploy DG1 overload". */
  @property() label = '';
  @property({ attribute: 'hint-on' }) hintOn = 'Hold to turn on';
  @property({ attribute: 'hint-off' }) hintOff = 'Hold to turn off';

  @state() private holding = false;
  @state() private busy = false;
  /** For one frame after `checked` flips: no transition, see `hold-switch.css`. */
  @state() private snap = false;
  @state() private hinted = false;
  /** Where the hint appears, in viewport pixels: under the pointer that let go. */
  @state() private hintAt: { x: number; y: number } | null = null;

  private timer: number | undefined;
  private hintTimer: number | undefined;
  private busyTimer: number | undefined;
  private fired = false;

  override disconnectedCallback(): void {
    window.clearTimeout(this.timer);
    window.clearTimeout(this.hintTimer);
    window.clearTimeout(this.busyTimer);
    super.disconnectedCallback();
  }

  protected override willUpdate(changed: PropertyValues<this>): void {
    // Cleared in the same render as the flip, so the knob is never drawn in
    // the new state at the end of a hold it has already finished.
    if (changed.has('checked') && this.busy) {
      this.busy = false;
      this.snap = true;
      window.clearTimeout(this.busyTimer);
    }
  }

  protected override updated(): void {
    if (this.snap) requestAnimationFrame(() => (this.snap = false));
  }

  private start(): void {
    if (this.disabled || this.busy || this.holding) return;
    this.fired = false;
    this.hinted = false;
    this.holding = true;
    this.timer = window.setTimeout(() => this.commit(), this.holdMs);
  }

  private stop(at?: { x: number; y: number }): void {
    if (!this.holding) return;
    window.clearTimeout(this.timer);
    this.holding = false;
    if (!this.fired) this.hint(at);
  }

  private commit(): void {
    this.fired = true;
    this.holding = false;
    this.busy = true;
    // The owner answers by changing `checked`; if it never does - a refusal -
    // the switch stops looking busy on its own.
    this.busyTimer = window.setTimeout(() => {
      this.busy = false;
    }, 6000);
    this.dispatchEvent(
      new CustomEvent('hold-commit', { detail: { checked: !this.checked }, bubbles: true }),
    );
  }

  private hint(at?: { x: number; y: number }): void {
    if (at) this.hintAt = at;
    else {
      // From the keyboard there is no pointer: under the switch instead.
      const box = this.getBoundingClientRect();
      this.hintAt = { x: box.left, y: box.bottom };
    }
    this.hinted = true;
    window.clearTimeout(this.hintTimer);
    this.hintTimer = window.setTimeout(() => {
      this.hinted = false;
    }, 1600);
  }

  override render(): TemplateResult {
    const classes = [
      'hs',
      this.checked ? 'is-on' : '',
      this.holding ? 'is-holding' : '',
      this.busy ? 'is-busy' : '',
      this.snap ? 'is-snap' : '',
      this.disabled ? 'is-disabled' : '',
    ].join(' ');
    return html`
      <button
        type="button"
        role="switch"
        class=${classes}
        style=${`--hold-ms:${this.holdMs}ms`}
        aria-checked=${this.checked ? 'true' : 'false'}
        aria-label=${this.label}
        aria-busy=${this.busy ? 'true' : 'false'}
        ?disabled=${this.disabled}
        title=${this.checked ? this.hintOff : this.hintOn}
        @pointerdown=${(e: PointerEvent) => {
          if (e.button !== 0) return;
          try {
            // Keep the hold even if the pointer drifts off the switch.
            (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
          } catch {
            // No capturable pointer (a synthetic event): hold without it.
          }
          this.start();
        }}
        @pointerup=${(e: PointerEvent) => this.stop({ x: e.clientX, y: e.clientY })}
        @pointercancel=${() => this.stop()}
        @lostpointercapture=${() => this.stop()}
        @keydown=${(e: KeyboardEvent) => {
          if ((e.key === ' ' || e.key === 'Enter') && !e.repeat) {
            e.preventDefault();
            this.start();
          }
        }}
        @keyup=${(e: KeyboardEvent) => {
          if (e.key === ' ' || e.key === 'Enter') this.stop();
        }}
        @click=${(e: Event) => {
          // Never flip on a click, and never let the click reach a row under it.
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        <span class="hs-track"><span class="hs-fill"></span></span>
        <span class="hs-knob"></span>
      </button>
      <span
        class="hs-hint ${this.hinted ? 'is-shown' : ''}"
        style=${this.hintAt ? `left:${this.hintAt.x}px;top:${this.hintAt.y + 18}px` : ''}
        aria-live="polite"
        >${this.hinted ? (this.checked ? this.hintOff : this.hintOn) : ''}</span
      >
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'hold-switch': HoldSwitch;
  }
}
